import { useState, useEffect, useCallback } from 'react';
import i18n from '@/i18n';

const STORAGE_KEY = '88health-language-preference';

interface LanguageDetectResult {
  language: string;
  setLanguage: (lang: string) => void;
  isDetecting: boolean;
  detectedRegion: string;
}

async function detectRegionByIP(): Promise<{ country: string; region: string } | null> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    clearTimeout(timeoutId);
    if (!response.ok) return null;
    const data = await response.json();
    return {
      country: data.country || '',
      region: data.country_name || '',
    };
  } catch {
    return null;
  }
}

function getDefaultLanguage(): string {
  const browserLang = navigator.language || (navigator as unknown as Record<string, string>).userLanguage || '';
  if (browserLang.startsWith('zh')) {
    if (browserLang.includes('Hans') || browserLang.includes('CN')) {
      return 'zh-Hans';
    }
    return 'zh-Hant';
  }
  return 'zh-Hant';
}

export function useLanguageDetect(): LanguageDetectResult {
  const [isDetecting, setIsDetecting] = useState(true);
  const [detectedRegion, setDetectedRegion] = useState('');
  const [language, setLangState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || 'zh-Hant';
  });

  // Sync local state with i18n — this is the key fix:
  // When other components call setLanguage(), i18n changes globally,
  // and this listener keeps all useLanguageDetect instances in sync.
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setLangState(lng);
      localStorage.setItem(STORAGE_KEY, lng);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setLangState(saved);
      i18n.changeLanguage(saved);
      setIsDetecting(false);
      return;
    }

    detectRegionByIP().then((regionData) => {
      let detectedLang = 'zh-Hant';
      if (regionData) {
        setDetectedRegion(regionData.region);
        const country = regionData.country.toUpperCase();
        if (country === 'CN' || country === 'CHINA') {
          detectedLang = 'zh-Hans';
        }
      } else {
        detectedLang = getDefaultLanguage();
      }
      setLangState(detectedLang);
      i18n.changeLanguage(detectedLang);
      localStorage.setItem(STORAGE_KEY, detectedLang);
      setIsDetecting(false);
    });
  }, []);

  const setLanguage = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
  }, []);

  return { language, setLanguage, isDetecting, detectedRegion };
}